import { onAuthStateChanged, signOut, User, multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { getApiBaseUrl, bumpAuthSessionEpoch } from '@/utils/apiUtils';
import { SESSION_TIMEOUT, performGlobalLogout } from '@/config/firebase';
import { cleanStaleAuthData } from '@/utils/cacheVersion';
import { devLog, devWarn, devError } from '@/utils/devLog';
import { notifyBackendLogout } from '@/services/logoutService';
import { analyticsService } from '@/services/analyticsService';
import { ANALYTICS_FEATURES, ANALYTICS_ACTIONS } from '@/constants/analytics';
import { queryClient } from '@/services/queryClient';
import * as Sentry from '@sentry/react';
import { isUserRole, type UserRole } from '@/types/userRole';

// Re-export UserRole so existing imports `from '@/contexts/AuthManager'` keep working.
export type { UserRole };

// Extend Window interface to include BroadcastChannel
interface WindowWithBroadcastChannel extends Window {
  BroadcastChannel?: typeof BroadcastChannel;
}

/**
 * Extract the target uid from a `globalLogoutAt` storage value (round-18 CX#8). New builds write
 * a tagged JSON payload `{ at, uid }`; older builds wrote a bare millisecond timestamp string.
 * Returns the uid (`string`, or `null` for an anonymous-origin logout) when the value is tagged,
 * or `undefined` for a legacy/untagged value — which the receiver treats as an unconditional
 * sign-out (back-compat). A legacy `String(Date.now())` value JSON-parses to a NUMBER (not an
 * object), so it correctly falls through to `undefined`.
 */
function parseCrossTabLogoutUid(raw: string): string | null | undefined {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'uid' in parsed) {
      const uid = (parsed as { uid?: unknown }).uid;
      return typeof uid === 'string' ? uid : null;
    }
  } catch {
    // Not JSON (legacy bare-timestamp value) → untagged.
  }
  return undefined;
}

export interface AuthState {
  user: User | null;
  isAdmin: boolean;
  isApproved: boolean;
  authToken: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  subscription: 'pilot' | 'pro' | 'enterprise';
  isSubscriptionLoading: boolean;
  organizationId: string | null;
  organizationName: string | null;
  /** The user's role from Firebase custom claims. Defaults to 'investor' when unauthenticated or unknown. */
  userRole: UserRole;
  /**
   * Whether the user is the scoped `raise_owner` persona (derived from `userRole`).
   * A `raise_owner` is walled off from the rest of the platform — the shell renders
   * the minimal `OwnerLayout` and the route guards bounce them back to `/owner`.
   * Such an account is NEVER `approved` and never holds a paid subscription.
   */
  isRaiseOwner: boolean;
  /** Whether the user has enrolled a TOTP MFA factor via Firebase. */
  isMFAEnrolled: boolean;
}

type AuthStateListener = (state: AuthState) => void;

const INITIAL_AUTH_STATE: AuthState = {
  user: null,
  isAdmin: false,
  isApproved: false,
  authToken: null,
  isLoading: true,
  isInitialized: false,
  subscription: 'pilot',
  isSubscriptionLoading: false,
  organizationId: null,
  organizationName: null,
  userRole: 'investor',
  isRaiseOwner: false,
  isMFAEnrolled: false,
};

/**
 * Centralized Authentication Manager
 * Replaces multiple scattered auth listeners with a single, efficient manager
 */
export class AuthManager {
  private static instance: AuthManager;
  private authState: AuthState = INITIAL_AUTH_STATE;
  private listeners: Set<AuthStateListener> = new Set();
  private unsubscribe: (() => void) | null = null;
  private authChangePromise: Promise<void> | null = null;
  private pendingAuthUser: User | null | undefined = undefined; // undefined means no pending change
  private lastActivity = Date.now();
  private idlePromptActive = false;
  private suppressExpiryUntil = 0;
  private tokenExpiresAt: number | null = null;
  private tokenRefreshPromise: Promise<void> | null = null; // Promise-based lock instead of boolean flag
  private isLoggingOut = false; // Guard against re-authentication during logout
  private approvalListenerUnsubscribe: (() => void) | null = null; // Firestore listener for pending → approved
  // round-19 #5 (§4.3 identity safety): the last RAW uid observed by the onAuthStateChanged
  // callback, BEFORE coalescing. The auth-session epoch is bumped on every raw uid transition
  // here (not post-coalescing in processAuthChange) so a rapid sign-out→sign-in that the
  // coalescing collapses to a single A→A processing pass still advances the epoch twice — one
  // per raw transition. `LAST_OBSERVED_UID_UNSET` (a sentinel distinct from every real uid AND
  // from `null`) marks "no callback observed yet", so the FIRST observed identity — including an
  // initial signed-out `null` — is correctly detected as a transition. See §handleAuthChange.
  private static readonly LAST_OBSERVED_UID_UNSET = Symbol('last-observed-uid-unset');
  private lastObservedUid: string | null | typeof AuthManager.LAST_OBSERVED_UID_UNSET =
    AuthManager.LAST_OBSERVED_UID_UNSET;

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  private constructor() {
    devLog('AuthManager: Initializing...');
    // Clean stale auth data on initialization
    cleanStaleAuthData();
    this.initializeAuthListener();
    this.initializeActivityTracking();
    this.initializeCrossTabSync();
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: AuthStateListener): () => void {
    this.listeners.add(listener);

    // Immediately notify with current state
    listener(this.authState);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current auth state
   */
  getCurrentState(): AuthState {
    return { ...this.authState };
  }

  /**
   * Initialize the single auth listener for the entire app
   */
  private initializeAuthListener() {
    devLog('AuthManager: Setting up auth listener...');
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    this.unsubscribe = onAuthStateChanged(auth, (user) => {
      devLog('AuthManager: Auth state changed, user:', user?.email || 'null');
      // round-19 #5 (§4.3 identity safety): bump the auth-session epoch on EVERY raw uid
      // transition, SYNCHRONOUSLY in the observer callback, BEFORE handleAuthChange coalescing
      // can collapse it. handleAuthChange keeps only the LATEST pending user while a change
      // processes, so a rapid reauth (A → null → A) that arrives mid-processing is coalesced to
      // a single A→A pass and would NEVER advance the epoch if the bump lived in
      // processAuthChange. Bumping per raw transition here catches both the null and the
      // re-emitted A — the epoch advances twice — so a stale same-uid request can no longer pass
      // the logout validator (uid && sessionEpoch) against the freshly re-authenticated session
      // (the CX#10 harm, reopened by coalescing). A pure token refresh does NOT fire this
      // callback with a changed uid, so it produces no spurious bump. Kept synchronous — no
      // await before the bump — so nothing can interleave between the observation and the bump.
      const observedUid = user?.uid ?? null;
      if (observedUid !== this.lastObservedUid) {
        this.lastObservedUid = observedUid;
        bumpAuthSessionEpoch();
      }
      this.handleAuthChange(user);
    });
  }

  /**
   * Handle Firebase auth state changes - single point of truth
   *
   * RACE CONDITION FIX: Instead of discarding auth changes that arrive while
   * another is processing, we queue the latest one and process it after the
   * current one completes. This ensures we never miss a logout event.
   */
  private async handleAuthChange(user: User | null) {
    // If there's an ongoing auth change, queue this one and wait
    if (this.authChangePromise) {
      // Store the pending user state (overwrite any previous pending state)
      // We only care about the LATEST state, not intermediate ones
      this.pendingAuthUser = user;
      devLog('AuthManager: Auth change queued, waiting for current processing to complete');
      await this.authChangePromise;
      return; // The processing loop will handle the pending state
    }

    // Process auth changes in a loop to handle any queued changes
    while (true) {
      // Clear any pending state since we're about to process
      this.pendingAuthUser = undefined;

      // Create a new promise for this auth change
      this.authChangePromise = this.processAuthChange(user);
      await this.authChangePromise;
      this.authChangePromise = null;

      // Check if a new auth change came in while we were processing
      if (this.pendingAuthUser !== undefined) {
        devLog('AuthManager: Processing queued auth change');
        user = this.pendingAuthUser;
        // Continue the loop to process the pending state
      } else {
        // No pending changes, we're done
        break;
      }
    }
  }

  private async processAuthChange(user: User | null) {
    devLog('AuthManager: Processing auth change for user:', user?.email || 'null');
    try {
      // Guard: If logout is in progress or was recently initiated (persisted across
      // page reloads via localStorage), reject any non-null user events. This prevents
      // Firebase from re-authenticating via stale IndexedDB persistence after signOut.
      //
      // The localStorage flag carries a millisecond timestamp (written at
      // logout start) and is treated as STALE after 30 seconds. Historical
      // bug: if logout was interrupted before ``onAuthStateChanged(null)``
      // fired (tab closed mid-flow, unsubscribe raced the signOut, etc.),
      // the legacy boolean-flag value ``'1'`` would stick forever and
      // force-signOut every subsequent login — users saw a "sign in →
      // instant bounce back to /login" loop with no way out short of
      // clearing localStorage manually. The timestamp TTL caps that
      // failure mode at ~30s of broken logins instead of permanent
      // breakage.
      const LOGOUT_PENDING_TTL_MS = 30_000;
      const rawFlag = (() => {
        try { return localStorage.getItem('arterra_logout_pending'); }
        catch { return null; }
      })();
      const logoutRecentlyInitiated = (() => {
        if (rawFlag === null) return false;
        // Legacy boolean value written by older builds: honour for one
        // auth event then fall through (the clear below evicts it).
        if (rawFlag === '1') return true;
        const ts = parseInt(rawFlag, 10);
        if (!Number.isFinite(ts)) return false;
        return Date.now() - ts < LOGOUT_PENDING_TTL_MS;
      })();
      if (user && (this.isLoggingOut || logoutRecentlyInitiated)) {
        devLog('AuthManager: Ignoring user auth event during logout — forcing signOut');
        try { localStorage.removeItem('arterra_logout_pending'); } catch { /* ignore */ }
        try {
          await signOut(auth);
        } catch {
          // signOut may fail if already signed out — safe to ignore
        }
        // Process as null user (logged out)
        user = null;
      } else if (rawFlag !== null && !logoutRecentlyInitiated) {
        // Stale flag from a much earlier logout; evict so it doesn't
        // re-fire on a future auth event (timestamp-based read already
        // ignored it, but leaving the key sitting in localStorage
        // makes debugging harder).
        try { localStorage.removeItem('arterra_logout_pending'); } catch { /* ignore */ }
      }

      // round-18 CX#10 / round-19 #5 (§4.3 identity safety): the auth-session epoch is bumped in
      // the RAW onAuthStateChanged observer (see initializeAuthListener), on every raw uid
      // transition, BEFORE handleAuthChange coalescing. It is NOT bumped here, post-coalescing:
      // a rapid reauth (A → null → A) that the coalescer collapses to a single A→A processing
      // pass would advance the epoch ZERO times if the bump lived here, leaving a stale same-uid
      // request able to tear down the freshly re-authenticated session. The raw-callback bump is
      // the authority — it fires once per raw transition (twice for A→null→A) even when the
      // processing is coalesced.

      if (!user) {
        devLog('AuthManager: No user, setting logged out state');

        // Clear the logout-pending flag — logout is complete
        localStorage.removeItem('arterra_logout_pending');
        this.isLoggingOut = false;

        // Tear down the approval listener — user is no longer authenticated
        this.stopApprovalListener();

        // Clear all React Query caches so the next user doesn't see stale data
        queryClient.clear();

        this.updateAuthState({
          user: null,
          isAdmin: false,
          isApproved: false,
          authToken: null,
          isLoading: false,
          isInitialized: true,
          subscription: 'pilot',
          isSubscriptionLoading: false,
          organizationId: null,
          organizationName: null,
          userRole: 'investor',
          isRaiseOwner: false,
          isMFAEnrolled: false,
        });

        // Clear Sentry user context on logout
        Sentry.setUser(null);

        // Clear activity timestamp when user logs out
        localStorage.removeItem('lastActivityTimestamp');
        return;
      }

      // If a different user signs in, wipe the previous user's cached data
      if (this.authState.user && this.authState.user.uid !== user.uid) {
        devLog('AuthManager: Different user detected, clearing query cache');
        queryClient.clear();
      }

      // Check if session is expired before processing
      if (this.isSessionExpired() && this.authState.isInitialized) {
        devLog('Session expired during auth state change. Logging out...');
        await this.performLogout();
        return;
      }

      try {
        devLog('AuthManager: Getting token and claims...');
        // Smart token refresh: use cached token by default, only force-refresh
        // if token is within 5 minutes of expiry. Firebase ID tokens last 1 hour.
        // getIdTokenResult(false) uses the cached token if still valid, avoiding
        // a 200-400ms network round-trip on every page load/auth state change.
        let idTokenResult = await user.getIdTokenResult(false);

        // Check if token needs proactive refresh (within 5 min of expiry)
        const expirationTime = new Date(idTokenResult.expirationTime).getTime();
        const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
        if (expirationTime < fiveMinutesFromNow) {
          devLog('AuthManager: Token near expiry, forcing refresh');
          idTokenResult = await user.getIdTokenResult(true);
        }

        const token = idTokenResult.token;
        devLog('AuthManager: Got token, claims:', idTokenResult.claims);

        // Extract and store token expiration time
        const finalExpirationTime = new Date(idTokenResult.expirationTime).getTime();
        this.tokenExpiresAt = finalExpirationTime;
        devLog(`Token will expire at: ${new Date(finalExpirationTime).toISOString()}`);

        // Determine user role and approval status.
        // `role` lives in Firebase custom claims and is mirrored from
        // `users/{uid}.role` in Firestore. Unknown / missing values fall
        // back to 'investor' (the most-restrictive non-admin role) so a
        // misconfigured account can never accidentally inherit elevated
        // privileges from this code path.
        const claimRole = idTokenResult.claims.role;
        const userRole: UserRole = isUserRole(claimRole) ? claimRole : 'investor';
        const isApproved = idTokenResult.claims.approved === true;
        const isAdmin = userRole === 'admin';
        // Scoped owner persona. Derived from the role claim so the rest of the
        // app (shell layout split + route guards) can wall the account off from
        // every non-owner surface without re-reading claims.
        const isRaiseOwner = userRole === 'raise_owner';

        devLog('AuthManager: User role:', userRole, 'isApproved:', isApproved, 'isAdmin:', isAdmin, 'isRaiseOwner:', isRaiseOwner);

        // Immediately update auth state — keep subscription loading until fetch completes
        // Try cached subscription first for instant UI if available
        let cachedSubscription: AuthState['subscription'] | null = null;
        try {
          const cacheKey = `arterra_subscription_${user.uid}`;
          cachedSubscription = localStorage.getItem(cacheKey) as AuthState['subscription'] | null;
          if (cachedSubscription !== 'pilot' && cachedSubscription !== 'pro' && cachedSubscription !== 'enterprise') {
            cachedSubscription = null;
          }
        } catch (cacheError) {
          devWarn('Subscription cache read failed', cacheError);
        }

        this.updateAuthState({
          user,
          isAdmin,
          isApproved,
          authToken: token,
          isLoading: false,
          isInitialized: true,
          // If we have a cached subscription, use it immediately and mark loading done.
          // Otherwise, keep loading true until the async fetch resolves.
          isSubscriptionLoading: cachedSubscription === null,
          subscription: cachedSubscription ?? 'pilot',
          userRole,
          isRaiseOwner,
          isMFAEnrolled: false, // Reset before async MFA check
        });
        devLog('AuthManager: Auth state updated, subscription cache:', cachedSubscription ? 'hit' : 'miss');

        // Always start the Firestore approval listener for any authenticated user.
        // This ensures that if an admin rejects or sets pending_deletion on an
        // account that is currently approved, the listener is running and will
        // detect the transition immediately — rather than waiting for the next
        // token refresh. The listener is a no-op if one is already running
        // (startApprovalListener has an internal single-listener guard).
        this.startApprovalListener(user.uid);

        // Check MFA enrollment status
        const enrolledFactors = multiFactor(user).enrolledFactors;
        const isMFAEnrolled = enrolledFactors.some(
          f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
        );
        this.updateAuthState({ isMFAEnrolled });

        // Set Sentry user context for error tracking.
        // §4.3 (round-9 #5a): attach ONLY the opaque Firebase UID — never the
        // email or display name. `sendDefaultPii:false` (config/sentry.ts) does
        // NOT strip user context we supply EXPLICITLY, so passing email/username
        // here would ship investor/LP PII to Sentry on every captured error. The
        // UID is sufficient to correlate errors to a user without exposing PII.
        Sentry.setUser({
          id: user.uid,
        });

        // Try to use cached org data immediately
        try {
          const orgCacheKey = `arterra_org_${user.uid}`;
          const cachedOrg = localStorage.getItem(orgCacheKey);
          if (cachedOrg) {
            const orgData = JSON.parse(cachedOrg);
            if (orgData.organizationId) {
              this.updateAuthState({
                organizationId: orgData.organizationId,
                organizationName: orgData.organizationName || null,
              });
            }
          }
        } catch (orgCacheError) {
          devWarn('Organization cache read failed', orgCacheError);
        }

        // Fetch fresh subscription and org data from backend asynchronously (don't await)
        const fetchSubscriptionAsync = async (): Promise<void> => {
          let retryCount = 0;
          const maxRetries = 2;

          const fetchSubscription = async (): Promise<void> => {
            try {
              const resp = await fetch(`${getApiBaseUrl()}/api/auth/user`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (resp.ok) {
                const data = await resp.json();
                const sub = (data?.user?.subscription as AuthState['subscription']) || 'pilot';
                this.updateAuthState({ subscription: sub, isSubscriptionLoading: false });
                try {
                  localStorage.setItem(`arterra_subscription_${user.uid}`, sub);
                } catch (cacheWriteErr) {
                  devWarn('Subscription cache write failed', cacheWriteErr);
                }

                // Extract organizationId (UUID) and organizationName from user data
                const orgId = data?.user?.organizationId || null;
                const orgName = data?.user?.organizationName || null;

                if (orgId || orgName) {
                  this.updateAuthState({
                    organizationId: orgId,
                    organizationName: orgName,
                  });

                  // Update Sentry context with organization
                  if (orgId) {
                    Sentry.setTag('organizationId', orgId);
                  }

                  // Cache org data
                  try {
                    localStorage.setItem(`arterra_org_${user.uid}`, JSON.stringify({
                      organizationId: orgId,
                      organizationName: orgName,
                    }));
                  } catch (orgCacheWriteErr) {
                    devWarn('Organization cache write failed', orgCacheWriteErr);
                  }
                }
              } else if (resp.status === 429 && retryCount < maxRetries) {
                // Rate limited - wait and retry
                retryCount++;
                await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                return fetchSubscription();
              } else {
                // Non-ok response — stop loading, fall back to current subscription value
                this.updateAuthState({ isSubscriptionLoading: false });
                devError('Subscription fetch failed with HTTP error:', {
                  userId: user.uid,
                  status: resp.status,
                  statusText: resp.statusText,
                  retryCount: retryCount,
                  endpoint: `${getApiBaseUrl()}/api/auth/user`
                });
              }
            } catch (subErr) {
              // Network/fetch error — stop loading, fall back to current subscription value
              this.updateAuthState({ isSubscriptionLoading: false });
              devError('Subscription fetch network error:', subErr, {
                userId: user.uid,
                retryCount: retryCount,
                endpoint: `${getApiBaseUrl()}/api/auth/user`
              });
              // Don't rethrow - subscription is optional, auth should still work
            }
          };

          await fetchSubscription();
        };

        // Fire and forget - don't await this
        fetchSubscriptionAsync().catch(err => {
          // Log unexpected errors in the async subscription fetch flow
          devError('Subscription background fetch error:', err, {
            userId: user.uid,
            context: 'Background subscription fetch failed unexpectedly'
          });
          // Don't rethrow - subscription is optional, auth should still work
        });

        // Update activity
        this.updateLastActivity();

      } catch (error) {
        devError('Error processing auth change:', error);
        this.updateAuthState({
          ...this.authState,
          isLoading: false,
          isInitialized: true,
          isSubscriptionLoading: false,
        });
      }
    } catch (error) {
      devError('Error in handleAuthChange:', error);
      this.updateAuthState({
        ...this.authState,
        isLoading: false,
        isInitialized: true,
        isSubscriptionLoading: false,
      });
    }
  }

  /**
   * Update auth state and notify all listeners
   */
  private updateAuthState(newState: Partial<AuthState>) {
    devLog('AuthManager: Updating state from', this.authState, 'to', newState);
    this.authState = { ...this.authState, ...newState };
    devLog('AuthManager: New state:', this.authState);
    this.notifyListeners();
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners() {
    devLog('AuthManager: Notifying', this.listeners.size, 'listeners');
    this.listeners.forEach(listener => {
      try {
        listener(this.authState);
      } catch (error) {
        devError('Error in auth state listener:', error);
      }
    });
  }

  /**
   * Initialize optimized activity tracking
   */
  private initializeActivityTracking() {
    // Debounced activity update (only update every 30 seconds max)
    let lastActivityUpdate = 0;
    const isShortTimeout = SESSION_TIMEOUT <= 20000; // test mode for fast iteration
    const ACTIVITY_THROTTLE = isShortTimeout ? 1000 : 30000; // 1s when testing, 30s in prod

    const updateActivity = () => {
      const now = Date.now();
      if (now - lastActivityUpdate > ACTIVITY_THROTTLE) {
        this.updateLastActivity();
        lastActivityUpdate = now;
      }
    };

    // Only essential events with passive listeners for performance
    const events = ['mousedown', 'keydown', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, {
        passive: true,
        capture: false
      });
    });

    // Store interval IDs for visibility management
    let warningIntervalId: NodeJS.Timeout | null = null;
    let expiryIntervalId: NodeJS.Timeout | null = null;

    // Function to start activity monitoring intervals
    const startActivityIntervals = () => {
      // Clear any existing intervals
      if (warningIntervalId) clearInterval(warningIntervalId);
      if (expiryIntervalId) clearInterval(expiryIntervalId);

      // Idle warning timing
      const CHECK_WARN_MS = isShortTimeout ? 1000 : 30000;
      warningIntervalId = setInterval(() => {
        if (!this.authState.user) return;
        const stored = localStorage.getItem('lastActivityTimestamp');
        const lastActivity = stored ? parseInt(stored, 10) : this.lastActivity;
        const timeSince = Date.now() - lastActivity;
        const timeLeft = SESSION_TIMEOUT - timeSince;
        const WARNING_BEFORE_MS = Math.min(2 * 60 * 1000, Math.max(5000, Math.floor(SESSION_TIMEOUT * 0.5)));
        if (timeLeft <= WARNING_BEFORE_MS && timeLeft > 0) {
          this.promptIdleWarning(Math.max(0, timeLeft));
        }
      }, CHECK_WARN_MS);

      // Check for session expiry
      const CHECK_EXP_MS = isShortTimeout ? 1000 : 60000;
      expiryIntervalId = setInterval(async () => {
        const now = Date.now();
        const expirySuppressed = this.idlePromptActive || (now < this.suppressExpiryUntil);

        if (!this.authState.user || expirySuppressed) return;

        // Check both activity timeout AND token expiration
        const activityExpired = this.isSessionExpired();
        const tokenExpiringSoon = this.isTokenExpiringSoon(5); // 5 min buffer

        if (activityExpired) {
          devLog('Session timeout due to inactivity. Logging out...');
          this.performLogout();
        } else if (tokenExpiringSoon && !this.tokenRefreshPromise) {
          // RACE CONDITION FIX: Use promise-based lock instead of boolean flag
          // This ensures only one refresh can run at a time, even if multiple
          // intervals fire simultaneously (e.g., when tab becomes visible)
          devLog('Token expiring soon. Attempting proactive refresh...');

          this.tokenRefreshPromise = (async () => {
            try {
              await this.refreshToken();
              devLog('Proactive token refresh successful');
            } catch (error) {
              devError('Proactive token refresh failed:', error);

              // Show warning dialog - user can choose to retry or logout
              const timeLeft = this.getTokenTimeRemaining();
              if (timeLeft > 60000) { // Only warn if >1 minute left
                const userWantsToStay = await this.promptTokenExpirationWarning(timeLeft);

                if (userWantsToStay) {
                  // User clicked "Stay Signed In" - try refresh again
                  try {
                    await this.refreshToken();
                    devLog('Manual token refresh after warning succeeded');
                  } catch (retryError) {
                    devError('Manual refresh also failed:', retryError);
                    // Let them hit 401 on next API call, which will trigger retry logic
                  }
                } else {
                  // User chose to logout or dialog timed out
                  this.performLogout();
                }
              }
              // If <1 minute left, don't show warning - let API retry handle it
            }
          })().finally(() => {
            // Clear the promise lock when done (success or failure)
            this.tokenRefreshPromise = null;
          });

          // Don't await - let it run in background so interval doesn't block
        }
      }, CHECK_EXP_MS);
    };

    // Function to stop activity monitoring intervals
    const stopActivityIntervals = () => {
      if (warningIntervalId) {
        clearInterval(warningIntervalId);
        warningIntervalId = null;
      }
      if (expiryIntervalId) {
        clearInterval(expiryIntervalId);
        expiryIntervalId = null;
      }
    };

    // Start intervals initially
    startActivityIntervals();

    // Pause/resume intervals based on tab visibility
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Tab is hidden, pause timers to save CPU/battery
        stopActivityIntervals();
        devLog('Tab hidden, pausing activity timers');
      } else {
        // Tab is visible again, resume timers
        startActivityIntervals();
        devLog('Tab visible, resuming activity timers');
        // Also update activity to prevent immediate logout
        this.updateLastActivity();
      }
    });
  }

  /**
   * Update last activity timestamp
   */
  private updateLastActivity() {
    this.lastActivity = Date.now();
    try {
      localStorage.setItem('lastActivityTimestamp', this.lastActivity.toString());
    } catch (error) {
      devError('Error updating activity timestamp:', error);
    }
  }

  /**
   * Public helper to record activity from anywhere (e.g., API wrappers)
   */
  recordActivity(): void {
    this.updateLastActivity();
  }

  /**
   * Refresh MFA enrollment status from Firebase.
   * Called by useMFA hook after enrollment/unenrollment changes.
   */
  async refreshMFAStatus(): Promise<void> {
    if (!this.authState.user) return;
    try {
      const enrolledFactors = multiFactor(this.authState.user).enrolledFactors;
      const isMFAEnrolled = enrolledFactors.some(
        f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      this.updateAuthState({ isMFAEnrolled });
    } catch (error) {
      devError('AuthManager: Failed to refresh MFA status:', error);
    }
  }

  /**
   * Cross-tab synchronization so logout propagates everywhere quickly
   */
  private initializeCrossTabSync() {
    try {
      // storage event fired in other tabs
      window.addEventListener('storage', (e) => {
        if (e.key === 'globalLogoutAt' && e.newValue) {
          devLog('AuthManager: Cross-tab logout signal received via storage event');
          // round-18 CX#8: the value is a tagged JSON payload `{at, uid}` (or a legacy bare
          // timestamp → undefined). Pass the target uid so a tab on a DIFFERENT account no-ops.
          this.handleCrossTabLogout(parseCrossTabLogoutUid(e.newValue));
        }
      });
      // BroadcastChannel for faster propagation when available
      const windowWithBc = window as unknown as WindowWithBroadcastChannel;
      const bc = windowWithBc.BroadcastChannel ? new BroadcastChannel('auth') : null;
      bc?.addEventListener('message', (event: MessageEvent) => {
        if (event.data?.type === 'logout') {
          devLog('AuthManager: Cross-tab logout signal received via BroadcastChannel');
          // round-18 CX#8: a new sender tags the message with the target uid (string | null); a
          // legacy sender omits it (undefined → unconditional sign-out, back-compat).
          const targetUid = (event.data as { uid?: string | null }).uid;
          this.handleCrossTabLogout(targetUid);
        }
      });
    } catch (err) {
      devWarn('Cross-tab auth sync initialization failed', err);
    }
  }

  /**
   * Handle logout signal received from another tab.
   *
   * CRITICAL FIX: This method addresses the cross-tab logout race condition by:
   * 1. Unsubscribing from Firebase onAuthStateChanged FIRST (prevents re-authentication)
   * 2. Calling firebase.signOut() in THIS tab (not just the initiating tab)
   * 3. Clearing AuthManager state
   * 4. Redirecting immediately (no delay that could allow race conditions)
   *
   * Without step 1 & 2, Firebase could re-emit auth state with cached user data
   * before the redirect completes, causing the user to appear logged in again.
   */
  private async handleCrossTabLogout(targetUid?: string | null): Promise<void> {
    // round-18 CX#8 / round-19 #7 (§4.3 identity safety): a cross-tab logout is scoped to the
    // account that INITIATED it. A TAGGED signal (`targetUid !== undefined`) must sign this tab
    // out ONLY when this tab's current identity is EXACTLY the target — otherwise no-op. Two
    // cases the earlier `currentUid !== null` allowance got wrong:
    //   • this tab signed in as B, tagged logout for A  → currentUid B ≠ A → no-op (unchanged).
    //   • this tab ANONYMOUS (currentUid null), tagged logout for A → the old guard let it FALL
    //     THROUGH (because `null !== null` is false) and signed the anonymous tab out + redirected
    //     it, disrupting unrelated anonymous browsing on another account's logout. Now null ≠ A
    //     → no-op, exactly as for the B case.
    // A tab mid-its-OWN logout still matches (its currentUid is still A at the instant the A-tagged
    // signal arrives → proceeds). An UNTAGGED signal (`undefined` — a legacy/rolling-deploy sender)
    // still signs out unconditionally (back-compat), since this branch is skipped entirely.
    if (targetUid !== undefined) {
      const currentUid = auth.currentUser?.uid ?? this.authState.user?.uid ?? null;
      if (currentUid !== targetUid) {
        devLog('AuthManager: Ignoring cross-tab logout for a different identity', {
          targetUid,
          currentUid,
        });
        return;
      }
    }
    try {
      // STEP 1: Unsubscribe from Firebase onAuthStateChanged IMMEDIATELY
      // This prevents Firebase from re-triggering with cached user data
      if (this.unsubscribe) {
        devLog('AuthManager: Unsubscribing from Firebase auth listener');
        this.unsubscribe();
        this.unsubscribe = null;
      }

      // STEP 1b: Stop the Firestore approval listener — explicit hygiene so the
      // listener does not linger and attempt Firestore reads as an unauthenticated user.
      this.stopApprovalListener();

      // STEP 2: Call firebase.signOut() in THIS tab
      // This clears Firebase's internal auth state and IndexedDB cache for this tab
      try {
        devLog('AuthManager: Calling firebase.signOut() in receiving tab');
        await signOut(auth);
      } catch (signOutErr) {
        // Log but don't block - signOut may fail if already signed out
        devWarn('AuthManager: signOut() in receiving tab encountered error:', signOutErr);
      }

      // STEP 3: Clear AuthManager state
      this.updateAuthState({
        user: null,
        isAdmin: false,
        isApproved: false,
        authToken: null,
        isLoading: false,
        subscription: 'pilot',
        isSubscriptionLoading: false,
        organizationId: null,
        organizationName: null,
        userRole: 'investor',
        isRaiseOwner: false,
        isMFAEnrolled: false,
      });

      // Clear Sentry user context
      Sentry.setUser(null);

      // Clear activity timestamp
      localStorage.removeItem('lastActivityTimestamp');

      // STEP 4: Redirect immediately - no delay
      // The 50ms delay was a source of race conditions where Firebase could
      // re-authenticate before redirect completed
      devLog('AuthManager: Redirecting to login page');
      window.location.href = '/login';
    } catch (err) {
      devError('AuthManager: Error during cross-tab logout handling:', err);
      // Force redirect even if cleanup fails
      window.location.href = '/login';
    }
  }

  /**
   * Show idle warning prompt to user. If they choose to stay, refresh token and bump activity.
   */
  private async promptIdleWarning(timeLeftMs: number) {
    this.idlePromptActive = true;
    // Avoid spamming: only show when < 2min and not already refreshed within this window
    const FLAG_KEY = 'idleWarningShownAt';
    const lastShown = parseInt(localStorage.getItem(FLAG_KEY) || '0', 10);
    const now = Date.now();
    if (now - lastShown < 60000) return;
    try {
      localStorage.setItem(FLAG_KEY, String(now));
    } catch {
      // Intentionally ignored - localStorage may be unavailable due to:
      // - Storage quota exceeded
      // - Private browsing mode
      // - Disabled by user/enterprise policy
      // This is non-critical for idle warning functionality
    }

    // Open custom modal via global event with native fallback
    let stay = false;
    try {
      stay = await new Promise<boolean>((resolve) => {
        try {
          const event = new CustomEvent('arterra:idle-warning', {
            detail: { timeLeftMs, resolve },
            cancelable: true
          });
          window.dispatchEvent(event);

          // If no handler prevented default, use native fallback
          if (!event.defaultPrevented) {
            devWarn('Idle warning modal not available, using native confirm dialog');
            const timeLeftSec = Math.ceil(timeLeftMs / 1000);
            const nativeResult = window.confirm(
              `Your session is about to expire in ${timeLeftSec} seconds due to inactivity.\n\nClick OK to stay logged in, or Cancel to log out.`
            );
            resolve(nativeResult);
            return;
          }

          // Safety timeout: if handler doesn't resolve, auto-resolve to false
          setTimeout(() => {
            devWarn('Idle warning modal handler did not resolve in time, defaulting to logout');
            resolve(false);
          }, Math.max(15000, Math.min(timeLeftMs, 60000)));

        } catch (err) {
          devError('Failed to dispatch idle warning event:', err);
          // Native fallback on event dispatch failure
          const timeLeftSec = Math.ceil(timeLeftMs / 1000);
          const nativeResult = window.confirm(
            `Your session is about to expire in ${timeLeftSec} seconds due to inactivity.\n\nClick OK to stay logged in, or Cancel to log out.`
          );
          resolve(nativeResult);
        }
      });
    } catch (err) {
      devError('Idle warning promise resolution failed:', err);
      // Final fallback - default to logout for safety
      stay = false;
    }

    try {
      if (stay) {
        // Immediately record activity and grant a short grace to cover refresh latency
        this.updateLastActivity();
        this.suppressExpiryUntil = Date.now() + 5000; // 5s grace
        await this.refreshToken();
        this.updateLastActivity();
      }
    } finally {
      this.idlePromptActive = false;
    }
  }

  /**
   * Show token expiration warning when proactive refresh fails
   * Uses SEPARATE event from idle warning to avoid listener conflicts
   */
  private async promptTokenExpirationWarning(timeLeftMs: number): Promise<boolean> {
    this.idlePromptActive = true; // Prevent both dialogs from showing

    devLog('Showing token expiration warning, time left:', timeLeftMs);

    try {
      const stay = await new Promise<boolean>((resolve) => {
        try {
          // Use DISTINCT event name to avoid breaking existing listeners
          const event = new CustomEvent('arterra:token-warning', {
            detail: {
              timeLeftMs,
              resolve,
              reason: 'token-expiration' // Explicit reason
            },
            cancelable: true
          });
          window.dispatchEvent(event);

          // Safety timeout
          setTimeout(() => resolve(false), Math.max(15000, Math.min(timeLeftMs, 60000)));
        } catch {
          resolve(false);
        }
      });

      return stay;
    } catch {
      return false;
    } finally {
      this.idlePromptActive = false;
    }
  }

  /**
   * Check if current session has expired
   */
  private isSessionExpired(): boolean {
    try {
      const stored = localStorage.getItem('lastActivityTimestamp');
      const lastActivity = stored ? parseInt(stored, 10) : this.lastActivity;
      const timeSinceLastActivity = Date.now() - lastActivity;
      return timeSinceLastActivity >= SESSION_TIMEOUT;
    } catch (error) {
      devError('Error checking session expiry:', error);
      return true; // Fail closed: treat as expired if we can't verify
    }
  }

  /**
   * Check if Firebase token is expired or will expire soon
   * Uses CACHED expiration time (no Firebase API call)
   */
  private isTokenExpiringSoon(bufferMinutes: number = 5): boolean {
    if (!this.tokenExpiresAt) return false;

    const now = Date.now();
    const bufferMs = bufferMinutes * 60 * 1000;
    const timeUntilExpiry = this.tokenExpiresAt - now;

    return timeUntilExpiry <= bufferMs;
  }

  /**
   * Update cached token expiration time (called by apiUtils after refresh)
   * Public method to keep monitoring in sync when external code refreshes token
   */
  public updateTokenExpiration(expirationTimeMs: number): void {
    this.tokenExpiresAt = expirationTimeMs;
    devLog('Token expiration updated externally:', new Date(expirationTimeMs).toISOString());
  }

  /**
   * Get time remaining until token expires (in milliseconds)
   */
  private getTokenTimeRemaining(): number {
    if (!this.tokenExpiresAt) return Infinity;
    return Math.max(0, this.tokenExpiresAt - Date.now());
  }

  /**
   * Perform logout and cleanup
   *
   * SECURITY: This method follows a specific order:
   * 1. Notify backend FIRST (while token is still valid) for server-side cleanup
   * 2. Clear local cached data
   * 3. Update local auth state
   * 4. Delegate to global logout for Firebase signOut and cross-tab sync
   */
  async performLogout(): Promise<void> {
    // CRITICAL: Set the logout guard FIRST to prevent any re-authentication.
    // This blocks processAuthChange from re-establishing a user session
    // if Firebase fires onAuthStateChanged with stale cached data.
    this.isLoggingOut = true;

    // Set persistent flag that survives page reloads. This handles the case where
    // window.location.replace fires before Firebase's IndexedDB persistence write
    // completes — on the fresh page, processAuthChange checks this flag and
    // forces a signOut instead of re-authenticating.
    //
    // Store the current millisecond timestamp as the value. The reader
    // in ``processAuthChange`` ignores flags older than ~30s so an
    // interrupted logout can't permanently brick future logins (see the
    // historical bug notes there). Reading side is still backward-
    // compatible with legacy boolean ``'1'`` values shipped by older
    // builds.
    try {
      localStorage.setItem('arterra_logout_pending', String(Date.now()));
    } catch {
      // Non-critical — the in-memory flag handles the same-page case
    }

    // Unsubscribe from onAuthStateChanged BEFORE any cleanup. This prevents
    // Firebase from re-triggering auth events during the logout flow (e.g.,
    // signOut() fires onAuthStateChanged(null) which could race with our
    // state updates, or worse, IndexedDB reads trigger onAuthStateChanged(user)).
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    try {
      // Track logout before clearing auth state so the user ID is still available
      analyticsService.trackFeatureUsage(
        ANALYTICS_FEATURES.AUTH,
        ANALYTICS_ACTIONS.AUTH.LOGOUT,
      );

      // Step 1: Notify backend FIRST (with current token, before clearing anything)
      // This allows the backend to:
      // - Blacklist the session token in Redis
      // - Invalidate server-side auth cache
      // - Clear HttpOnly cookies via Set-Cookie headers
      // - Create audit log entry
      const logoutResult = await notifyBackendLogout();
      if (!logoutResult.backendNotified) {
        devWarn('Backend logout notification failed:', logoutResult.error);
      } else {
        devLog('Backend logout notification successful');
      }

      // Step 2: Clear any cached subscription and org data for all users
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('arterra_subscription_') || key.startsWith('arterra_org_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Step 3: Update local auth state immediately
      this.updateAuthState({
        user: null,
        isAdmin: false,
        isApproved: false,
        authToken: null,
        isLoading: false,
        subscription: 'pilot',
        isSubscriptionLoading: false,
        organizationId: null,
        organizationName: null,
        userRole: 'investor',
        isRaiseOwner: false,
        isMFAEnrolled: false,
      });

      // Step 4: Delegate to global logout (Firebase signOut + cross-tab sync)
      await performGlobalLogout();
    } catch (error) {
      devError('Error during logout in AuthManager:', error);
      // Clear auth state even on error — prevents sidebar showing on /login
      this.updateAuthState({
        user: null,
        isAdmin: false,
        isApproved: false,
        authToken: null,
        isLoading: false,
        subscription: 'pilot',
        isSubscriptionLoading: false,
        organizationId: null,
        organizationName: null,
        userRole: 'investor',
        isRaiseOwner: false,
        isMFAEnrolled: false,
      });
      // Force redirect even if cleanup fails
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
  }

  /**
   * Clear auth state and cleanup
   */
  clearState(): void {
    this.updateAuthState(INITIAL_AUTH_STATE);
    localStorage.removeItem('lastActivityTimestamp');
  }

  /**
   * Get the current auth token (uses cached value; refreshes if near expiry).
   * Convenience method for components that call `authManager.getToken()`.
   */
  async getToken(): Promise<string | null> {
    if (this.authState.authToken && !this.isTokenExpiringSoon(2)) {
      return this.authState.authToken;
    }
    return this.refreshToken();
  }

  /**
   * Force-refresh the Firebase ID token AND re-derive the role-bearing auth state
   * from the fresh custom claims.
   *
   * Unlike {@link refreshToken} (which only swaps the cached token string), this
   * re-reads `role` / `approved` from the refreshed claims and recomputes
   * `userRole` / `isAdmin` / `isRaiseOwner` / `isApproved`. It is the supported way
   * to propagate a SERVER-SIDE custom-claim change into the app WITHOUT a full page
   * reload — `onAuthStateChanged` does NOT fire on a token refresh, so a claim
   * change is otherwise invisible until the next sign-in.
   *
   * Used by the campaign-invite funnel: accepting an invite stamps the
   * `raise_owner` claim server-side, and the client must pick up the new persona
   * (so the route-wall renders the OwnerLayout) before routing to the console.
   * Mirrors the partial refresh the approval listener already performs for the
   * `approved` claim, generalized to the full role-bearing claim set.
   *
   * RESOLVING THE USER — the funnel can call this immediately after
   * `createUserWithEmailAndPassword`, BEFORE `onAuthStateChanged` has populated
   * `authState.user`. Early-returning in that window would leave the freshly-stamped
   * role unread and strand the claimant. We therefore resolve the user from (in
   * order) an explicit argument, the observed `authState.user`, then the live
   * `auth.currentUser` — only bailing when genuinely signed out.
   *
   * RETURN — resolves to the re-derived `isRaiseOwner` so callers (the invite funnel)
   * can gate navigation on the confirmed role without re-reading state. The previous
   * `Promise<void>` callers (the `useAuth` wrapper / approval flow) are unaffected by
   * this additive return. On a no-op/failure it returns the last-known `isRaiseOwner`.
   */
  async refreshClaims(firebaseUser?: User | null): Promise<boolean> {
    const user = firebaseUser ?? this.authState.user ?? auth.currentUser;
    if (!user) {
      devWarn('AuthManager: refreshClaims called with no observable user — skipping');
      return this.authState.isRaiseOwner;
    }
    try {
      const idTokenResult = await user.getIdTokenResult(true);
      const token = idTokenResult.token;
      this.tokenExpiresAt = new Date(idTokenResult.expirationTime).getTime();

      const claimRole = idTokenResult.claims.role;
      const userRole: UserRole = isUserRole(claimRole) ? claimRole : 'investor';
      const isRaiseOwner = userRole === 'raise_owner';

      this.updateAuthState({
        // Adopt the resolved user so a refresh that ran ahead of the auth observer
        // (brand-new account) leaves the app in a consistent signed-in state.
        user,
        authToken: token,
        userRole,
        isAdmin: userRole === 'admin',
        isRaiseOwner,
        isApproved: idTokenResult.claims.approved === true,
      });
      this.updateLastActivity();
      return isRaiseOwner;
    } catch (error) {
      devError('AuthManager: refreshClaims failed:', error);
      return this.authState.isRaiseOwner;
    }
  }

  /**
   * Refresh the current user's token
   */
  async refreshToken(): Promise<string | null> {
    if (!this.authState.user) return null;

    const refreshStartTime = Date.now();
    devLog('Starting token refresh for user:', this.authState.user.uid);

    try {
      // Get token AND expiration metadata with a single Firebase call
      // getIdTokenResult returns both token and expirationTime, no need for separate getIdToken call
      const idTokenResult = await this.authState.user.getIdTokenResult(true);
      const token = idTokenResult.token;

      // Update cached expiration time
      const expirationTime = new Date(idTokenResult.expirationTime).getTime();
      this.tokenExpiresAt = expirationTime;

      const refreshTime = Date.now() - refreshStartTime;
      devLog(`Token refreshed successfully in ${refreshTime}ms. New expiry: ${new Date(expirationTime).toISOString()}`);

      this.updateAuthState({ authToken: token });
      this.updateLastActivity();
      return token;
    } catch (error) {
      const refreshTime = Date.now() - refreshStartTime;
      devError(`Token refresh failed after ${refreshTime}ms:`, error);
      return null;
    }
  }

  /**
   * Start a Firestore listener on users/{uid} while the account is pending.
   *
   * The listener detects two transitions:
   *   pending → approved  : force-refresh the Firebase ID token (picks up new
   *                         custom claims), update React Query cache for the
   *                         user-profile, show a toast, and redirect to home.
   *   any     → pending_deletion : show a cancellable banner via a custom
   *                         DOM event so the user can cancel within 30 days.
   *
   * The listener is started once when we first detect a pending user and is
   * automatically torn down on approval, logout, or destroy().
   */
  startApprovalListener(uid: string): void {
    // Guard: only one listener at a time
    if (this.approvalListenerUnsubscribe) return;

    devLog('AuthManager: Starting approval listener for uid:', uid);

    try {
      const userDocRef = doc(db, 'users', uid);

      this.approvalListenerUnsubscribe = onSnapshot(
        userDocRef,
        async (snapshot) => {
          if (!snapshot.exists()) return;

          const data = snapshot.data();
          const newStatus: string | undefined = data?.status;

          devLog('AuthManager: Approval listener — status:', newStatus);

          if (newStatus === 'approved') {
            // Tear down the listener immediately so it doesn't fire twice
            this.stopApprovalListener();

            try {
              const user = this.authState.user;
              if (!user) return;

              // Force token refresh to incorporate the new `approved` claim
              devLog('AuthManager: Account approved — force-refreshing ID token');
              const idTokenResult = await user.getIdTokenResult(true);
              const token = idTokenResult.token;

              // Update AuthManager state with the new approved status
              const expirationTime = new Date(idTokenResult.expirationTime).getTime();
              this.tokenExpiresAt = expirationTime;
              this.updateAuthState({
                authToken: token,
                isApproved: true,
              });

              // Invalidate React Query cache so user-profile re-fetches
              queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });

              // Notify the UI via a custom event so any mounted component can
              // show a toast and redirect without tight coupling to AuthManager.
              window.dispatchEvent(
                new CustomEvent('arterra:account-approved', { detail: { uid } })
              );

            } catch (err) {
              devError('AuthManager: Token refresh after approval failed:', err);
            }

          } else if (newStatus === 'rejected') {
            // Admin has rejected this account — sign the user out immediately.
            // The Firebase Auth account is also disabled server-side, so any
            // subsequent API calls would fail with 401. Signing out here ensures
            // the UI reflects that state without waiting for the next token refresh.
            devLog('AuthManager: Account rejected — signing out');

            // IMPORTANT: stop the listener BEFORE calling signOut() so that if
            // signOut() fails (e.g. network error), the listener does not keep
            // re-firing on the same Firestore snapshot and triggering duplicate
            // sign-out attempts or dispatching the event more than once.
            this.stopApprovalListener();

            try {
              await auth.signOut();
            } catch (signOutErr) {
              devWarn('AuthManager: signOut() after rejection encountered error:', signOutErr);
            }
            // Dispatch via DOM event so any mounted component can show a toast
            // without introducing a circular dependency on sonner from this module.
            window.dispatchEvent(
              new CustomEvent('arterra:account-rejected', { detail: { uid } })
            );
          } else if (newStatus === 'pending_deletion') {
            // Notify the UI so a banner can be shown with a cancel CTA
            window.dispatchEvent(
              new CustomEvent('arterra:account-pending-deletion', { detail: { uid } })
            );
          }
        },
        (err) => {
          // Firestore permission errors (e.g. user logged out mid-listen) are
          // expected — log at warn level and tear down gracefully.
          devWarn('AuthManager: Approval listener error:', err);
          this.stopApprovalListener();
        }
      );
    } catch (err) {
      devError('AuthManager: Failed to start approval listener:', err);
    }
  }

  /**
   * Stop the Firestore approval listener and release resources.
   */
  stopApprovalListener(): void {
    if (this.approvalListenerUnsubscribe) {
      devLog('AuthManager: Stopping approval listener');
      this.approvalListenerUnsubscribe();
      this.approvalListenerUnsubscribe = null;
    }
  }

  /**
   * Cleanup when app is destroyed
   */
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.stopApprovalListener();
    this.listeners.clear();
  }
}
